import { useUnit } from 'effector-react'
import React, { lazy, Suspense, useState } from 'react'
import ReactGA from 'react-ga4'
import TagManager from 'react-gtm-module'
import { useTranslation } from 'react-i18next'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import styles from './App.module.scss'
import {
	$user,
	checkProfessionFx,
	userFx,
	userLanaguageEvent,
	SupportedLanguage,
} from "./features/app/model";
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';
import { formatFingerprintForAPI, logFingerprintData, handleFingerprintError } from './utils/fingerprint';
import { getAdParam, storeAdParamInLocalStorage } from "./utils/ads-params";
import { LanguageRoute } from './components/LanguageRoute'
import { UpgradePopup, UpsellPopup, StealthModelPopup } from '@components';
import { setUpgradePopupCallback } from './shared/ui/toast';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
// not Lazily loaded components
import { Header_shadowgpt } from '@components'
import { Humanizer} from './pages/Landing'; 
import BrevoPopover from './components/BrevoPopover'
import { $hasProfession } from './features/app/model';
import { RequireAuth } from './components/ReqiureAuth';
import $api from "./http";

// Lazy load components with error handling
const Footer = lazy(() => {
	const loadComponent = (retries = 3): Promise<{ default: any }> => {
		return import("@components")
			.then((m) => {
				if (!m.Footer) {
					throw new Error("Footer component not found");
				}
				return { default: m.Footer };
			})
			.catch((error) => {
				console.error("Failed to load Footer:", error);
				if (retries > 0) {
					return new Promise((resolve) => {
						setTimeout(() => {
							resolve(loadComponent(retries - 1));
						}, 1000);
					});
				}
				// Return empty component if Footer fails to load
				return Promise.resolve({ default: () => null });
			});
	};
	return loadComponent();
});

//static imports

// Add type safety for dynamic imports with error handling and retry
const createLazyPage = <K extends keyof Omit<typeof import("@pages"), "Humanizer">>(
	componentName: K,
) =>
	lazy(() => {
		const loadComponent = (retries = 3): Promise<{ default: any }> => {
			return import("@pages")
				.then((m) => {
					if (!m[componentName]) {
						throw new Error(`Component ${componentName} not found in @pages`);
					}
					return {
						default: m[componentName],
					};
				})
				.catch((error) => {
					console.error(`Failed to load component ${componentName}:`, error);
					if (retries > 0) {
						console.log(`Retrying to load ${componentName}... (${retries} retries left)`);
						return new Promise((resolve) => {
							setTimeout(() => {
								resolve(loadComponent(retries - 1));
							}, 1000);
						});
					}
					// If all retries failed, return ErrorPage component
					return import("./pages/ErrorPage").then((m) => ({
						default: m.ErrorPage,
					}));
				});
		};
		return loadComponent();
	});

const Auth = createLazyPage("Auth");
const Humanizer_shadowgpt = createLazyPage("Humanizer_shadowgpt");
const Humanizer_transform = createLazyPage("Humanizer_transform");
const Humanizer_gptzero = createLazyPage("Humanizer_gptzero");
const Humanizer_bypass = createLazyPage("Humanizer_bypass");
const Humanizer_aichanger = createLazyPage("Humanizer_aichanger");
const Humanizer_createundetectable = createLazyPage("Humanizer_createundetectable");

const ContentLoader = React.memo(() => (
	<div className={styles.contentLoader}>
		<div className={styles.loadingBar} />
		<div className={styles.loadingBar} />
		<div className={styles.loadingBar} />
	</div>
));

// Main pages
const PastRequests = createLazyPage("PastRequests");
const DodgeAiChekers = createLazyPage("DodgeAiChekers");
const MediaMentions = createLazyPage("MediaMentions");
const ChooseUsSection = createLazyPage("ChooseUsSection");
const FeaturesSection = createLazyPage("FeaturesSection");
const CaseStudySection = createLazyPage("CaseStudySection");
const TextExampleSection = createLazyPage("TextExampleSection");
const ComparisonTableSection = createLazyPage("ComparisonTableSection");
const ShadowsReference = createLazyPage("ShadowsReference");
const ShadowGPTReview = createLazyPage("ShadowGPTReview");
const FAQBlock = createLazyPage("FAQBlock");
const PricingBlock = createLazyPage("PricingBlock");
const StayInShadowBlock = createLazyPage("StayInShadowBlock");
const GenerationBlock = createLazyPage("GenerationBlock");
const Pricing = createLazyPage("Pricing");
const ShadowGPT = createLazyPage("ShadowGPT");
const Billings = createLazyPage("Billings");
const Support = createLazyPage("Support");
const AccountDetails = createLazyPage("AccountDetails");
const History = createLazyPage("History");
const ErrorPage = createLazyPage("ErrorPage");
const TermsAndPrivicyPages = createLazyPage("TermsAndPrivicyPages");
const TermsAndConditions = createLazyPage("TermsAndConditions");
const PrivacyPolicy = createLazyPage("PrivacyPolicy");

// ShadowGPT variants
const PastRequests_shadowgpt = createLazyPage("PastRequests_shadowgpt");
const DodgeAiChekers_shadowgpt = createLazyPage("DodgeAiChekers_shadowgpt");
const ChooseUsSection_shadowgpt = createLazyPage("ChooseUsSection_shadowgpt");
const ShadowsReference_shadowgpt = createLazyPage("ShadowsReference_shadowgpt");
const ShadowGPTReview_shadowgpt = createLazyPage("ShadowGPTReview_shadowgpt");
const FAQBlock_shadowgpt = createLazyPage("FAQBlock_shadowgpt");
const StayInShadowBlock_shadowgpt = createLazyPage("StayInShadowBlock_shadowgpt");

// Bypass variants
const TextWall_bypass = createLazyPage("TextWall_bypass");
const FAQBlock_bypass = createLazyPage("FAQBlock_bypass");
const StayInShadowBlock_bypass = createLazyPage("StayInShadowBlock_bypass");

// GPTZero variants
const FAQBlock_gptzero = createLazyPage("FAQBlock_gptzero");
const TextWall_gptzero = createLazyPage("TextWall_gptzero");

// AI Changer variants
const FAQBlock_aichanger = createLazyPage("FAQBlock_aichanger");
const TextWall_aichanger = createLazyPage("TextWall_aichanger");

// Undetectable AI variants
const FAQBlock_createundetectable = createLazyPage("FAQBlock_createundetectable");
const TextWall_createundetectable = createLazyPage("TextWall_createundetectable");

// Transformer variants
const TextWall_transform = createLazyPage("TextWall_transform");
const FAQBlock_transform = createLazyPage("FAQBlock_transform");

const SummarizerPage = lazy(() =>
	import("./pages/Summarizer").then((m) => ({ default: m.default })),
);
const ParagraphGeneratorPage = lazy(() =>
	import("./pages/ParagraphGenerator").then((m) => ({ default: m.default })),
);
const TextMessageGeneratorPage = lazy(() =>
	import("./pages/TextMessageGenerator").then((m) => ({ default: m.default })),
);
const AbstractGeneratorPage = lazy(() =>
	import("./pages/AbstractGenerator").then((m) => ({ default: m.default })),
);
const AiPhotoSolverPage = lazy(() =>
	import("./pages/AiPhotoSolver").then((m) => ({ default: m.default })),
);

// Special language pages TextWalls
const TextWall_Spanish = lazy(() =>
	import("./pages/Landing_bypass/TextWall_Spanish").then((m) => ({ default: m.TextWall_Spanish })),
);
const TextWall_Portuguese = lazy(() =>
	import("./pages/Landing_bypass/TextWall_Portuguese").then((m) => ({
		default: m.TextWall_Portuguese,
	})),
);

const privateRoute = [
	{
		path: "/support",
		element: (
			<RequireAuth>
				<Suspense fallback={<ContentLoader />}>
					<Support />
				</Suspense>
			</RequireAuth>
		),
	},
	{
		path: "/generation/",
		element: (
			<RequireAuth>
				<Suspense fallback={<ContentLoader />}>
					<GenerationBlock />
				</Suspense>
			</RequireAuth>
		),
		children: [
			{
				path: "/generation/shadow-chat",
				element: (
					<Suspense fallback={<ContentLoader />}>
						<ShadowGPT />
					</Suspense>
				),
			},
			{
				path: "/generation/billings",
				element: (
					<Suspense fallback={<ContentLoader />}>
						<Billings />
					</Suspense>
				),
			},
			{
				path: "/generation/account-details",
				element: (
					<Suspense fallback={<ContentLoader />}>
						<AccountDetails />
					</Suspense>
				),
			},
			{
				path: "/generation/history",
				element: (
					<Suspense fallback={<ContentLoader />}>
						<History />
					</Suspense>
				),
			},
			{
				path: "/generation/ai-photo-solver",
				element: (
					<Suspense fallback={<ContentLoader />}>
						<AiPhotoSolverPage />
					</Suspense>
				),
			},
		],
	},
];
const publicRoute = [
	{
		path: "/",
		element: (
			<>
				<Header_shadowgpt />
				<Humanizer />
				<Suspense fallback={<ContentLoader />}>
					<>
						<MediaMentions />
						<DodgeAiChekers />
						<ShadowsReference />
						<ShadowGPTReview />
						<PastRequests />
						<ChooseUsSection />
						<FeaturesSection />
						<CaseStudySection />
						<TextExampleSection />
						<ComparisonTableSection />
						<FAQBlock />
						<PricingBlock />
						<StayInShadowBlock />
						<Footer />
					</>
				</Suspense>
			</>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "/:lang",
		element: (
			<LanguageRoute>
				<>
					<Header_shadowgpt />
					<Humanizer />
					<Suspense fallback={<ContentLoader />}>
						<>
							<MediaMentions />
							<DodgeAiChekers />
							<ShadowsReference />
							<ShadowGPTReview />
							<PastRequests />
							<ChooseUsSection />
							<FeaturesSection />
							<CaseStudySection />
							<TextExampleSection />
							<ComparisonTableSection />
							<FAQBlock />
							<PricingBlock />
							<StayInShadowBlock />
							<Footer />
						</>
					</Suspense>
				</>
			</LanguageRoute>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "/shadowgpt",
		element: (
			<>
				<Header_shadowgpt />
				<Humanizer_shadowgpt />
				<DodgeAiChekers_shadowgpt />
				<ShadowsReference_shadowgpt />
				<PastRequests_shadowgpt />
				<ShadowGPTReview_shadowgpt />
				<ChooseUsSection_shadowgpt />
				<FAQBlock_shadowgpt />
				<PricingBlock />
				<StayInShadowBlock_shadowgpt />
				<Footer />
			</>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "/paraphrasing-tool",
		element: (
			<>
				<Header_shadowgpt />
				<Humanizer_transform />
				<TextWall_transform />
				<FAQBlock_transform />
				<PricingBlock />
				<StayInShadowBlock />
				<Footer />
			</>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "/ai-detector",
		element: (
			<>
				<Header_shadowgpt />
				<Humanizer_aichanger />
				<TextWall_aichanger />
				<FAQBlock_aichanger />
				<PricingBlock />
				<StayInShadowBlock />
				<Footer />
			</>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "/pricing",
		element: <Pricing />,
	},
	{
		path: "/auth",
		element: <Auth />,
	},
	{
		path: "/summarizer",
		element: (
			<Suspense fallback={<ContentLoader />}>
				<SummarizerPage />
			</Suspense>
		),
	},
	{
		path: "/:lang/summarizer",
		element: (
			<LanguageRoute>
				<Suspense fallback={<ContentLoader />}>
					<SummarizerPage />
				</Suspense>
			</LanguageRoute>
		),
	},
	{
		path: "/paragraph-generator",
		element: (
			<Suspense fallback={<ContentLoader />}>
				<ParagraphGeneratorPage />
			</Suspense>
		),
	},
	{
		path: "/:lang/paragraph-generator",
		element: (
			<LanguageRoute>
				<Suspense fallback={<ContentLoader />}>
					<ParagraphGeneratorPage />
				</Suspense>
			</LanguageRoute>
		),
	},
	{
		path: "/abstract-generator",
		element: (
			<Suspense fallback={<ContentLoader />}>
				<AbstractGeneratorPage />
			</Suspense>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "/:lang/abstract-generator",
		element: (
			<LanguageRoute>
				<Suspense fallback={<ContentLoader />}>
					<AbstractGeneratorPage />
				</Suspense>
			</LanguageRoute>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "/text-message-generator",
		element: (
			<Suspense fallback={<ContentLoader />}>
				<TextMessageGeneratorPage />
			</Suspense>
		),
	},
	{
		path: "/:lang/text-message-generator",
		element: (
			<LanguageRoute>
				<Suspense fallback={<ContentLoader />}>
					<TextMessageGeneratorPage />
				</Suspense>
			</LanguageRoute>
		),
	},
	{
		path: "/terms",
		element: <TermsAndPrivicyPages />,
		children: [
			{
				element: <TermsAndConditions />,
				path: "/terms/conditions",
			},
			{
				element: <PrivacyPolicy />,
				path: "/terms/privacy",
			},
		],
	},
	{
		path: "/:lang/terms",
		element: (
			<LanguageRoute>
				<TermsAndPrivicyPages />
			</LanguageRoute>
		),
		children: [
			{
				element: (
					<LanguageRoute>
						<TermsAndConditions />
					</LanguageRoute>
				),
				path: "/:lang/terms/conditions",
			},
			{
				element: (
					<LanguageRoute>
						<PrivacyPolicy />
					</LanguageRoute>
				),
				path: "/:lang/terms/privacy",
			},
		],
	},
	{
		path: "/bypass-ai-detection",
		element: (
			<>
				<Header_shadowgpt />
				<Humanizer_bypass />
				<TextWall_bypass />
				<FAQBlock_bypass />
				<PricingBlock />
				<StayInShadowBlock />
				<Footer />
			</>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "/transform-your-writing",
		element: (
			<>
				<Header_shadowgpt />
				<Humanizer_transform />
				<TextWall_transform />
				<FAQBlock_transform />
				<PricingBlock />
				<StayInShadowBlock />
				<Footer />
			</>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "/create-undetectable-content",
		element: (
			<>
				<Header_shadowgpt />
				<Humanizer_createundetectable />
				<TextWall_createundetectable />
				<FAQBlock_createundetectable />
				<PricingBlock />
				<StayInShadowBlock />
				<Footer />
			</>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "/gptzero-alternative",
		element: (
			<>
				<Header_shadowgpt />
				<Humanizer_gptzero />
				<TextWall_gptzero />
				<FAQBlock_gptzero />
				<PricingBlock />
				<StayInShadowBlock />
				<Footer />
			</>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "/humanizador-de-texto",
		element: (
			<>
				<Header_shadowgpt />
				<Humanizer />
				<Suspense fallback={<ContentLoader />}>
					<TextWall_Spanish />
				</Suspense>
				<Suspense fallback={<ContentLoader />}>
					<Footer />
				</Suspense>
			</>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "/Humanizar-Texto-com-IA",
		element: (
			<>
				<Header_shadowgpt />
				<Humanizer />
				<Suspense fallback={<ContentLoader />}>
					<TextWall_Portuguese />
				</Suspense>
				<Suspense fallback={<ContentLoader />}>
					<Footer />
				</Suspense>
			</>
		),
		errorElement: <ErrorPage />,
	},
	{
		path: "*",
		element: <ErrorPage />,
	},
];

function App() {
	if (process.env.REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID) {
		ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID);
	}
	if (process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID) {
		TagManager.initialize({ gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID })
	}

    const { 
        isLoading: isFingerprintLoading, 
        error: fingerprintError, 
        data: fingerprintData, 
    } = useVisitorData(
        { extendedResult: true },
        { immediate: true }
    );

	const hasProfession = useUnit($hasProfession);
	const [showBrevoPopover, setShowBrevoPopover] = useState(false);
	const [isCheckingProfession, setIsCheckingProfession] = useState(true);
	const user = useUnit($user);
	const userRequest = useUnit(userFx);
	const { i18n } = useTranslation();
	const [showUpgradePopup, setShowUpgradePopup] = useState(false);
	const [upgradePopupTitle, setUpgradePopupTitle] = useState<string | undefined>();
	const [upgradePopupSubtitle, setUpgradePopupSubtitle] = useState<string | undefined>();
	const [showUpsellPopup, setShowUpsellPopup] = useState(false);
	const [showStealthModelPopup, setShowStealthModelPopup] = useState(false);
	const [showConfetti, setShowConfetti] = useState(false);
	const { width, height } = useWindowSize();

	// Настройка callback для UpgradePopup
	React.useEffect(() => {
		setUpgradePopupCallback((isOpen: boolean, title?: string, subtitle?: string) => {
			setShowUpgradePopup(isOpen);
			if (title) setUpgradePopupTitle(title);
			if (subtitle) setUpgradePopupSubtitle(subtitle);
		});
	}, []);

	// Обработка успешной покупки
	React.useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
	
		if (urlParams.has("new_subscription")) {
			// ⭐ НОВОЕ: Track purchase in Google Ads immediately (don't wait for HumanizerInput)
			const pendingPurchasePlanString = localStorage.getItem('pendingPurchasePlan');
			if (pendingPurchasePlanString && !sessionStorage.getItem('ads_purchase_tracked_new_sub')) {
				try {
					const pendingPurchasePlan = JSON.parse(pendingPurchasePlanString);
					const purchaseParams = {
						transaction_id: pendingPurchasePlan.sessionId,
						value: pendingPurchasePlan.amount,
						currency: pendingPurchasePlan.currency.code,
						items: [
							{
								item_id: pendingPurchasePlan.subscriptionId,
								item_name: pendingPurchasePlan.title,
								price: pendingPurchasePlan.amount,
							},
						],
					};
	
					// Fire analytics events immediately for Stripe purchases
					ReactGA.event('purchase', purchaseParams);
					if (window.dataLayer) {
						window.dataLayer.push({
							event: 'ads_purchase',
							transaction_id: purchaseParams.transaction_id,
							value: purchaseParams.value,
							currency: purchaseParams.currency,
							items: purchaseParams.items,
							user_email: user?.email,
						});
						console.log('🚀 Fired ads_purchase event for Stripe/Paddle in App.tsx', purchaseParams);
					}
					
					// Mark as tracked to prevent duplicate events
					sessionStorage.setItem('ads_purchase_tracked_new_sub', 'true');
				} catch (error) {
					console.error("Error processing pending purchase plan in App.tsx:", error);
				}
			}
			// ⭐ конец нового блока
	
			const unwatch = userFx.done.watch(({ result: user }) => {
				// Check if the user has a subscription and it has a title
				if (user && user.subscription && user.subscription.title) {
					console.log('App.tsx: User data is available, checking for upsell.');
					console.log('Subscription Title:', user.subscription.title);
	
					// Logic to show the popup
					const isWeekly = user.subscription.title.toLowerCase().includes('weekly');
					const isMonthly = user.subscription.title.toLowerCase().includes('monthly');
	
					if (isWeekly || isMonthly) {
						const lastShown = localStorage.getItem('upsellLastShown');
						const now = new Date().getTime();
						const oneDay = 24 * 60 * 60 * 1000;
	
						if (!lastShown || now - Number(lastShown) > oneDay) {
							console.log('Showing upsell popup.');
							setShowUpsellPopup(true); // Show the popup
							localStorage.setItem('upsellLastShown', String(now));
						} else {
							console.log('Upsell popup was shown less than 24 hours ago.');
						}
					}
					unwatch(); // Unsubscribe after checking
				} else {
					console.log('App.tsx: User data or subscription title not available yet for upsell check.');
				}
			});
	
			// Cleanup the watcher when the component unmounts or dependencies change
			return () => {
				unwatch();
			};
		}
	}, [user]);

	React.useEffect(() => {
		const paramsToStore: ('gclid' | 'gbraid' | 'wbraid')[] = ['gclid', 'gbraid', 'wbraid'];
		paramsToStore.forEach(paramName => {
			const paramValue = getAdParam(paramName);
			if (paramValue) {
				storeAdParamInLocalStorage(paramName, paramValue);
			}
		});
	}, []);

	React.useEffect(() => {
		const token = localStorage.getItem("token");
		const handleAuth = async () => {
			if (token) {
				console.log("Token found, fetching user data.");
				try {
					const res: any = await userRequest();
					if (res && Object.keys(res).length > 0) {
						console.log("Full user object from existing token flow:", res);
						userLanaguageEvent(
							res.language.code.toLowerCase() as SupportedLanguage,
						);
						i18n.changeLanguage(res.language.code.toLowerCase());
					}
				} catch (error) {
					console.error("Failed to fetch user with existing token:", error);
				}
			} else {
				console.log("No token found, starting anonymous login flow.");

				if (isFingerprintLoading) {
					console.log("Waiting for FingerprintJS Pro to load...");
					return;
				}

				if (fingerprintError) {
					console.error("FingerprintJS Pro error:", handleFingerprintError(fingerprintError));
					return;
				}

				if (!fingerprintData?.visitorId) {
					console.warn("FingerprintJS Pro: No visitor ID available yet");
					return;
				}

				const fingerprintPayload = formatFingerprintForAPI(fingerprintData);
				logFingerprintData(fingerprintData, 'Anonymous Login');
				console.log("FingerprintJS Pro visitorId:", fingerprintPayload.fingerprint);

				try {
					const res = await $api.api.authAnonymousControllerCreateAnonymousUserAndLogin({
						fingerprint: fingerprintPayload.fingerprint,
						requestId: fingerprintPayload.requestId,
					});

					console.log("Anonymous login API response:", res);
					const loginData: any = res.data.data || res.data;

					if (loginData && loginData.accessToken) {
						console.log("Received tokens, storing them.");
						localStorage.setItem("token", loginData.accessToken);
						localStorage.setItem("refresh", loginData.refreshToken);
						
						console.log("Tokens stored, fetching user data for new anonymous user.");
						const userRes: any = await userRequest();
						console.log("User data fetched after anonymous login:", userRes);
						if (userRes && Object.keys(userRes).length > 0) {
							console.log("User subscription after fetch:", userRes.subscription);
							userLanaguageEvent(
								userRes.language.code.toLowerCase() as SupportedLanguage,
							);
							i18n.changeLanguage(userRes.language.code.toLowerCase());
						}
					} else {
						console.error("No login data or access token found in response");
					}
				} catch (error) {
					console.error("Anonymous authentication flow failed:", error);
				}
			}
		};

		handleAuth();
	}, [userRequest, i18n, fingerprintData, isFingerprintLoading, fingerprintError]);


	// Запускаем проверку профессии при загрузке
	React.useEffect(() => {
		const token = localStorage.getItem("token");
		if (token) {
			setIsCheckingProfession(true);
			checkProfessionFx().finally(() => {
				setIsCheckingProfession(false);
			});
		} else {
			setIsCheckingProfession(false);
		}
	}, [user]); // Rerun when user object is populated

	// Управляем видимостью поповера
	React.useEffect(() => {
		const token = localStorage.getItem("token");
		if (
			token &&
			user &&
			user.role !== "ANONYMOUS" &&
			!hasProfession &&
			!isCheckingProfession
		) {
			setShowBrevoPopover(true);
		} else {
			setShowBrevoPopover(false);
		}
	}, [hasProfession, isCheckingProfession, user]);


     // This is a test function for UpsellPopup.
     // Uncomment the code below to enable `window.showUpsellPopup()` in the console.
     //
     const showUpsellPopupTest = () => {
         console.log('showUpsellPopupTest called, setting showUpsellPopup to true');
         setShowUpsellPopup(true);
     };

     const showStealthModelPopupTest = () => {
         console.log('showStealthModelPopupTest called, setting showStealthModelPopup to true');
         setShowStealthModelPopup(true);
     };

    const triggerConfetti = () => {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000); // Show confetti for 3 seconds
    };

    const handleStealthModelUpgrade = () => {
        console.log('StealthModel upgrade clicked, opening upgrade modal');
        setShowStealthModelPopup(false);
        setShowUpgradePopup(true);
        setUpgradePopupTitle('Upgrade to Pro');
        setUpgradePopupSubtitle('Unlock Stealth Model and all premium features');
    };
     //
     React.useEffect(() => {
         console.log('Binding showUpsellPopup to window');
        (window as any).showUpsellPopup = showUpsellPopupTest;
        (window as any).showStealthModelPopup = showStealthModelPopupTest;
        (window as any).triggerConfetti = triggerConfetti;
        (window as any).showStealthModelPopupFromHumanizer = showStealthModelPopupTest;
        (window as any).showUpgradePopup = (title?: string, subtitle?: string) => {
            setShowUpgradePopup(true);
            if (title) setUpgradePopupTitle(title);
            if (subtitle) setUpgradePopupSubtitle(subtitle);
        };
        (window as any).hideUpgradePopup = () => {
            setShowUpgradePopup(false);
        };
         console.log('window.showUpsellPopup:', (window as any).showUpsellPopup);
         console.log('window.showStealthModelPopup:', (window as any).showStealthModelPopup);
     }, []);

    return (
        <>
            {showConfetti && <Confetti 
                width={width} 
                height={height} 
                style={{ position: 'fixed', top: 0, left: 0, zIndex: 1000001, pointerEvents: 'none' }}
                gravity={0.3}
                initialVelocityY={20}
                numberOfPieces={150}
                recycle={false}
                tweenDuration={3000}
            />}
            <div
                style={{ position: 'relative', top: '50px', zIndex: '100000' }}
            >
                <ToastContainer
                    position="top-center"
                    theme="dark"
                    autoClose={4000}
                    hideProgressBar={true}
                />
            </div>

            {showBrevoPopover && (
        <BrevoPopover 
          onClose={() => setShowBrevoPopover(false)} 
        />
      )}
             <UpgradePopup 
                 isOpen={showUpgradePopup} 
                 onClose={() => setShowUpgradePopup(false)}
                 title={upgradePopupTitle}
                 subtitle={upgradePopupSubtitle}
                 triggerConfetti={triggerConfetti}
             />
             {/* The UpsellPopup is currently triggered only via the test function. */}
             <UpsellPopup 
                 isOpen={showUpsellPopup} 
                 onClose={() => setShowUpsellPopup(false)}
                 triggerConfetti={triggerConfetti}
             />
             <StealthModelPopup 
                 isOpen={showStealthModelPopup} 
                 onClose={() => setShowStealthModelPopup(false)}
                 onUpgrade={handleStealthModelUpgrade}
             />
            <div className={styles.background}>
                <RouterProvider
                    router={createBrowserRouter([...publicRoute, ...privateRoute])}
                />
            </div>
        </>
    )
}

export default App
